import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { useScheduleDetail, useTripConditions } from "../../hooks/queries";
import { useCreateTrip, useUpdateTrip } from "../../hooks/mutation";
import { supabase } from "../../supabaseClient";
import { easternLocalToUtc } from "../Utils/import";

import HeaderContainer from "../CrewDashboard/HeaderContainer";

import TextInput from "../common/TextInput";
import CustomSelect from "../common/select";

const FORM_FMT = "YYYY-MM-DD HH:mm:ss";
const DATE_PART_FMT = "YYYY-MM-DD";
const TIME_PART_FMT = "HH:mm";
const TIME_DISPLAY_FMT = "h:mm A";

// Parse a loosely-typed time string. Accepts every bulk-entry shape the
// manager actually types: "2:30 PM", "2:30pm", "230pm", "230 pm",
// "14:30", "1430", "9am", "9 AM", "12pm" (noon), "12am" (midnight).
// Returns "HH:mm" (24h) or null.
function parseTypedTime(input) {
  if (!input) return null;
  // Drop all whitespace so "2:30 pm", "2:30pm", "230 pm" all collapse to one.
  const s = input.trim().toLowerCase().replace(/\s+/g, "");
  if (!s) return null;

  const apply = (h, mi, ap) => {
    if (h > 23 || mi > 59) return null;
    if (ap === "pm" && h < 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}`;
  };

  // With colon: "2:30pm", "14:30", "2:30"
  let m = s.match(/^(\d{1,2}):(\d{2})(am|pm)?$/);
  if (m) return apply(parseInt(m[1], 10), parseInt(m[2], 10), m[3]);

  // No colon: "230pm" (h=2, m=30), "1430" (h=14, m=30), "9am" (h=9, m=0).
  m = s.match(/^(\d{1,4})(am|pm)?$/);
  if (m) {
    const digits = m[1];
    const ap = m[2];
    let h, mi;
    if (digits.length <= 2) { h = parseInt(digits, 10); mi = 0; }
    else if (digits.length === 3) { h = parseInt(digits[0], 10); mi = parseInt(digits.slice(1), 10); }
    else { h = parseInt(digits.slice(0, 2), 10); mi = parseInt(digits.slice(2), 10); }
    return apply(h, mi, ap);
  }
  return null;
}

// Format an "HH:mm" 24h string as "h:mm A" without depending on dayjs's
// customParseFormat plugin (which isn't loaded). Returns "" for empty or
// malformed input — never the literal "Invalid Date" that was leaking
// into the input box.
function formatTimeForDisplay(hhmm) {
  if (!hhmm) return "";
  const m = String(hhmm).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  let h = parseInt(m[1], 10);
  const mi = parseInt(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(mi) || h > 23 || mi > 59) return "";
  const ap = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${String(mi).padStart(2, "0")} ${ap}`;
}

// Inline month-grid calendar with MULTI-SELECT. Click a day to toggle it
// in/out of the selected set. `value` is an array of "YYYY-MM-DD" strings
// (in any order). Adjacent-month days remain clickable so you can pick
// across a month boundary without flipping the view first.
const CalendarPicker = ({ value, onChange }) => {
  const selectedSet = useMemo(() => new Set(value || []), [value]);
  const firstSelected = (value && value.length) ? dayjs(value[0]) : null;
  const [viewMonth, setViewMonth] = useState(() =>
    (firstSelected && firstSelected.isValid() ? firstSelected : dayjs()).startOf("month")
  );

  const start = viewMonth.startOf("week");
  const days = Array.from({ length: 42 }, (_, i) => start.add(i, "day"));
  const today = dayjs();
  const prev = viewMonth.subtract(1, "month");
  const next = viewMonth.add(1, "month");

  const toggle = (iso) => {
    const cur = value || [];
    if (cur.includes(iso)) onChange(cur.filter((d) => d !== iso));
    else onChange([...cur, iso].sort());
  };

  const linkStyle = { color: "#1e3a8a", textDecoration: "underline", cursor: "pointer", fontWeight: 500 };
  const headerStyle = { color: "#1e3a8a", fontSize: "18px", fontWeight: 700 };

  return (
    <div style={{ width: 280, border: "1px solid #d1d5db", borderRadius: 8, padding: 8, background: "#fff" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", alignItems: "center", padding: "4px 6px", marginBottom: 4 }}>
        <span onClick={() => setViewMonth(prev)} style={linkStyle}>{prev.format("MMM")}</span>
        <span style={{ ...headerStyle, textAlign: "center" }}>{viewMonth.format("MMMM YYYY")}</span>
        <span onClick={() => setViewMonth(next)} style={{ ...linkStyle, textAlign: "right" }}>{next.format("MMM")}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
          <div key={d} style={{ textAlign: "center", fontWeight: 600, fontSize: 12, color: "#374151", padding: "4px 0" }}>{d}</div>
        ))}
        {days.map((d) => {
          const iso = d.format(DATE_PART_FMT);
          const inMonth = d.month() === viewMonth.month();
          const isSelected = selectedSet.has(iso);
          const isToday = d.isSame(today, "day");
          const bg = isSelected ? "#2563eb" : (isToday ? "#eff6ff" : "transparent");
          const color = isSelected ? "#ffffff" : (inMonth ? "#111827" : "#9ca3af");
          return (
            <button
              key={iso}
              type="button"
              onClick={() => toggle(iso)}
              style={{
                background: bg, color, textDecoration: "underline",
                border: "none", borderRadius: 4, padding: "6px 0",
                fontSize: 13, cursor: "pointer", textAlign: "center",
                fontWeight: isSelected ? 700 : 400,
              }}
            >
              {d.date()}
            </button>
          );
        })}
      </div>
      {value && value.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#374151", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{value.length} day{value.length === 1 ? "" : "s"} selected</span>
          <button
            type="button"
            onClick={() => onChange([])}
            style={{ background: "transparent", border: "none", color: "#dc2626", cursor: "pointer", textDecoration: "underline", fontSize: 12 }}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

// Time text input. Accepts "230pm", "2:30 PM", "1430", "14:30", "9am",
// case-insensitive, with or without space before am/pm. Stores as "HH:mm"
// (24h); displays as "h:mm A".
const TimeTextInput = ({ value, onChange, error }) => {
  const [text, setText] = useState(() => formatTimeForDisplay(value));
  const [parseError, setParseError] = useState(null);
  // Track previous text length so we can tell typing from deleting. When
  // the user backspaces past the "m" in "pm" the raw value briefly ends
  // in "p" — without this, the auto-fill logic below would re-append the
  // "m" and trap the user (they'd have to delete from the middle).
  const prevLenRef = useRef((formatTimeForDisplay(value) || "").length);

  useEffect(() => {
    const display = formatTimeForDisplay(value);
    if (display && display !== text) {
      setText(display);
      prevLenRef.current = display.length;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = () => {
    const trimmed = text.trim();
    if (!trimmed) { setParseError(null); onChange(""); return; }
    const parsed = parseTypedTime(trimmed);
    if (parsed) {
      setParseError(null);
      onChange(parsed);
      setText(formatTimeForDisplay(parsed));
    } else {
      setParseError("Try: 2:30 PM or 1430");
    }
  };

  // Auto-fill the meridiem: typing a lone "a" or "p" after a numeric time
  // fills in "am" / "pm" without needing to type the "m". Fires whenever
  // the last typed char is a/A/p/P and the input before it already
  // contains a digit — so "3:38 p", "230a", "9p", "3:38p" all get "m"
  // auto-appended. Skipped when the string already ends in "am" / "pm" so
  // we don't loop, and skipped when the "a"/"p" is the very first char
  // (they might be typing something else entirely).
  const handleChange = (e) => {
    const raw = e.target.value;
    const isShrinking = raw.length < prevLenRef.current;
    prevLenRef.current = raw.length;
    const last = raw.slice(-1);
    const before = raw.slice(0, -1);
    if (
      !isShrinking &&
      (last === "a" || last === "A" || last === "p" || last === "P") &&
      /\d/.test(before) &&
      !/[ap]m$/i.test(before)
    ) {
      const next = raw + "m";
      prevLenRef.current = next.length;
      setText(next);
      return;
    }
    setText(raw);
  };

  return (
    <div className="flex flex-col gap-1">
      <input
        type="text"
        value={text}
        onChange={handleChange}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
        placeholder="2:30 PM"
        className="custom-input rounded-lg w-100"
      />
      {parseError && <p className="error-content">{parseError}</p>}
    </div>
  );
};

const TripAddContainer = () => {
  const { data: conditions, isLoading: loadingConditions } =
    useTripConditions();

  return <TripAdd conditions={conditions} isLoading={loadingConditions} />;
};

// Text input with inline "ghost" autocomplete for flight numbers.
//   - User types "3" → sees "3424" with "424" highlighted (from prior most-used
//     match); pressing Tab (or clicking away) accepts the completion.
//   - Typing another char replaces the highlighted portion, so "3" → "35" if
//     they hit "5" next, and the next best match is offered from there.
//   - Backspace and Delete skip the suggestion so the user can shrink the value.
// The datalist dropdown is filtered by the user's TYPED prefix (not the
// completed value), so alternatives to the ghost match still show — otherwise
// Chrome would hide the dropdown as soon as the input matches a single option.
function FlightNoAutocomplete({ value, onChange, options, className, error }) {
  const inputRef = useRef(null);
  const suppressCompleteRef = useRef(false);
  // Track the user-typed prefix (not the auto-appended suggestion suffix).
  // Drives selection highlight AND datalist filtering.
  const [typedPrefix, setTypedPrefix] = useState(value || "");
  const typedLen = typedPrefix.length;

  const handleKeyDown = useCallback((e) => {
    suppressCompleteRef.current =
      e.key === "Backspace" || e.key === "Delete" || e.key === "Escape";
  }, []);

  const handleChange = useCallback(
    (e) => {
      const raw = e.target.value;
      if (suppressCompleteRef.current) {
        setTypedPrefix(raw);
        onChange(raw);
        return;
      }
      const match = options.find(
        (fn) =>
          fn.length > raw.length &&
          fn.toLowerCase().startsWith(raw.toLowerCase())
      );
      setTypedPrefix(raw);
      onChange(raw && match ? match : raw);
    },
    [onChange, options]
  );

  // If the parent resets the form (e.g. save/clear), realign typed prefix.
  useEffect(() => {
    const v = value || "";
    if (typedPrefix.length > v.length || !v.toLowerCase().startsWith(typedPrefix.toLowerCase())) {
      setTypedPrefix(v);
    }
  }, [value, typedPrefix]);

  // After the browser paints the new value, restore the selection so only the
  // auto-appended suffix is highlighted.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (typedLen < (value || "").length && document.activeElement === el) {
      el.setSelectionRange(typedLen, value.length);
    }
  }, [value, typedLen]);

  // Datalist is scoped to the TYPED prefix so alternatives to the current
  // ghost stay visible. Empty prefix → full ranked list.
  const filtered = typedPrefix
    ? options.filter((fn) => fn.toLowerCase().startsWith(typedPrefix.toLowerCase()))
    : options;
  // Unique datalist id per render is unnecessary; a single stable id works.
  const listId = "flight-no-history";

  return (
    <div className="flex flex-col gap-1">
      <input
        ref={inputRef}
        type="text"
        value={value || ""}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className={className ?? ""}
        list={listId}
        autoComplete="off"
      />
      <datalist id={listId}>
        {filtered.map((fn) => (
          <option key={fn} value={fn} />
        ))}
      </datalist>
      {!!error && <p className="error-content">{error}</p>}
    </div>
  );
}

const TripAdd = ({ conditions, isLoading }) => {
  const navigate = useNavigate(); // Use useNavigate instead of useHistory
  const queryClient = useQueryClient();

  const { id: scheduleID } = useParams();

  const { data: scheduleDetail, isSuccess: successScheduleData } =
    useScheduleDetail(scheduleID);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      CompanyID: conditions ? conditions.companyList[0]?.CompanyID : 1,
      airline: conditions ? conditions.airlineList[0]?.AirlineID : 1,
      // The crew QR portal decides direction by pickup/dropoff location,
      // not by this field. Keep ARRIVE as the inherited default — admins
      // who actually need an ARRIVE row don't have to remember to flip it.
      option: "1",
      pu: conditions ? conditions.locationList[0]?.LocationID : 5,
      do: conditions ? conditions.locationList[0]?.LocationID : 5,
      flight_no: "",
      rate: "",
      driver: conditions ? conditions.driverList[0]?.DriverID : 1,
      vehicle: conditions ? conditions.vehicleList[0]?.VehicleID : 1,
      passengers: "",
      // `dates` is an array of YYYY-MM-DD strings (multi-select calendar).
      // `time` is HH:mm 24h. handleSave combines each date with `time` and
      // creates one schedule row per date, so the same trip can be cloned
      // across many days in a single submit.
      dates: [],
      time: dayjs().format("HH:mm"),
      managerId: "",
    },
  });

  // Trips added during this Add-Trip session — shown as a "just added"
  // strip at the top of the page so the manager gets confirmation without
  // navigating away. Reset when leaving the page.
  const [recentAdds, setRecentAdds] = useState([]);

  // Flight-number autocomplete: pull recent flight numbers scoped to the
  // currently-selected P/U and D/O locations so the <datalist> only offers
  // flights that historically flew through those stations. Falls back to a
  // manager-scoped (or global for admins) list when no locations are picked
  // yet. History is deduped on the bare number, then re-suffixed with
  // "-ADHOC" so the suggestion matches what actually gets saved. Sorted
  // most-used first with recency as the tiebreak; capped at 100 items.
  const puLoc = watch("pu");
  const doLoc = watch("do");
  const [flightNoOptions, setFlightNoOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const currentManagerId = parseInt(sessionStorage.getItem("UserId"));
      const isAdmin = sessionStorage.getItem("UserRole") === "1";
      const locIds = [puLoc, doLoc]
        .map((v) => parseInt(v))
        .filter((n) => Number.isFinite(n));
      const useLocScope = locIds.length > 0;
      // With a location filter, query ScheduleMixed (has pickup/dropoff
      // location IDs inline). Without one, hit the base Schedule table.
      let q;
      if (useLocScope) {
        q = supabase
          .from("ScheduleMixed")
          .select("FlightNo")
          .not("FlightNo", "is", null)
          .or(
            locIds
              .flatMap((id) => [`pickuplocationid.eq.${id}`, `dropofflocationid.eq.${id}`])
              .join(",")
          )
          .order("ScheduleDate", { ascending: false })
          .limit(2000);
      } else {
        q = supabase
          .from("Schedule")
          .select("FlightNo")
          .not("FlightNo", "is", null)
          .order("ScheduleDate", { ascending: false })
          .limit(2000);
      }
      if (!isAdmin && currentManagerId) {
        q = q.eq("ManagerID", currentManagerId);
      }
      const { data, error } = await q;
      if (error) console.error("flight-no history fetch failed:", error);
      if (cancelled || !data) return;
      const counts = new Map();
      const firstIdx = new Map();
      for (let i = 0; i < data.length; i++) {
        const raw = (data[i].FlightNo || "").trim();
        if (!raw) continue;
        const bare = raw.replace(/-ADHOC$/i, "");
        if (!bare) continue;
        counts.set(bare, (counts.get(bare) || 0) + 1);
        if (!firstIdx.has(bare)) firstIdx.set(bare, i);
      }
      // Suggest the ADHOC form ("1234-ADHOC"), since that's exactly what an
      // Add-Trip row saves as. Dedupe happened on the bare number above, so
      // "1234" and "1234-ADHOC" in history collapse to one suggestion.
      const list = [...counts.keys()]
        .sort((a, b) => {
          const d = counts.get(b) - counts.get(a);
          return d !== 0 ? d : firstIdx.get(a) - firstIdx.get(b);
        })
        .slice(0, 100)
        .map((bare) => `${bare}-ADHOC`);
      console.log(
        `flight-no history: ${list.length} suggestions (from ${data.length} rows, locs=${locIds.join("/") || "none"})`
      );
      setFlightNoOptions(list);
    })();
    return () => { cancelled = true; };
  }, [puLoc, doLoc]);

  // Searchable P/U and D/O options. The location list can be 500+ rows, so a
  // plain <select> is unwieldy; CustomSelect (react-select) adds type-to-filter.
  const locationOptions = useMemo(
    () =>
      (conditions?.locationList || []).map((_location) => ({
        label: _location.Code,
        value: _location.LocationID,
      })),
    [conditions]
  );

  // Reset form with proper defaults when conditions load (create mode)
  useEffect(() => {
    if (!scheduleID && conditions && !isLoading) {
      reset({
        CompanyID: conditions.companyList[0]?.CompanyID || "",
        airline: conditions.airlineList[0]?.AirlineID || "",
        option: "1",
        pu: conditions.locationList[0]?.LocationID || "",
        do: conditions.locationList[0]?.LocationID || "",
        flight_no: "",
        rate: "",
        driver: conditions.driverList[0]?.DriverID || "",
        vehicle: conditions.vehicleList[0]?.VehicleID || "",
        passengers: "",
        dates: [],
        time: dayjs().format("HH:mm"),
        managerId: "",
      });
    }
  }, [conditions, isLoading, scheduleID, reset]);

  // Reset form with schedule data (edit mode)
  useEffect(() => {
    if (successScheduleData) {
      reset({
        CompanyID: scheduleDetail.CompanyID,
        airline: scheduleDetail.AirlineID,
        option: `${scheduleDetail.TransportationTypeID}`,
        pu: scheduleDetail.Pickups[0]
          ? scheduleDetail.Pickups[0].Location.LocationID
          : conditions?.locationList[0]?.LocationID || "",
        do: scheduleDetail.Dropoffs[0]
          ? scheduleDetail.Dropoffs[0].Location.LocationID
          : conditions?.locationList[0]?.LocationID || "",
        flight_no: scheduleDetail.FlightNo,
        rate: scheduleDetail.RateOverride,
        driver: scheduleDetail.DriverID,
        vehicle: scheduleDetail.VehicleID,
        passengers: scheduleDetail.Pickups[0]
          ? scheduleDetail.Pickups[0].Passengers
          : 1,
        dates: [dayjs(scheduleDetail.ScheduleDate).format("YYYY-MM-DD")],
        time: dayjs(scheduleDetail.ScheduleDate).format("HH:mm"),
      });
    }
  }, [scheduleDetail, reset, successScheduleData, conditions]);

  // Edit-mode save navigates back, just like before.
  const onUpdateSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["reportConditions"] });
    queryClient.invalidateQueries({ queryKey: ["searchConditions"] });
    toast.success("Trip updated successfully!");
    setTimeout(() => {
      sessionStorage.getItem("UserRole") === "1"
        ? navigate("/super-admin")
        : navigate("/manager");
    }, 1500);
  }, [navigate, queryClient]);
  const onError = useCallback((error) => {
    console.error("Trip save error:", error);
    const msg = error?.message || "Unknown error";
    toast.error(scheduleID ? `Failed to update trip: ${msg}` : `Failed to add trip: ${msg}`);
  }, [scheduleID]);
  // Use mutateAsync on create so we can await N sequential inserts (one per
  // selected date) and only show ONE summary toast at the end. The hook's
  // per-row onSuccess stays a no-op for creates.
  const { mutateAsync: createTrip, isPending: isCreating } = useCreateTrip(
    null,
    onError
  );
  const { mutateAsync: updateTrip, isPending: isUpdating } = useUpdateTrip(
    onUpdateSuccess,
    onError
  );

  // Resolve the selected airline's letter code so the mutation knows whether
  // to treat the entered time as a pickup (B6) vs flight time, and so the
  // form can relabel the date field accordingly.
  const selectedAirlineId = watch("airline");
  const selectedAirlineCode = (
    conditions?.airlineList?.find(
      (a) => String(a.AirlineID) === String(selectedAirlineId)
    )?.AirlineLetterCode || ""
  ).toUpperCase();
  const isB6 = selectedAirlineCode === "B6";
  const dateLabel = isB6 ? "Pickup Date/Time" : "Flight Date/Time";

  const handleSave = useCallback(
    async (_data) => {
      const { dates, time, ...rest } = _data;
      if (!time) {
        toast.error("Please enter a time");
        return;
      }
      if (!dates || dates.length === 0) {
        toast.error("Pick at least one date on the calendar");
        return;
      }

      // Derive TransportationTypeID (ARRIVE vs DEPART) from the chosen PU/DO
      // locations using their LocationTypeID (1 = HOTEL, 2 = AIRPORT).
      const locById = (conditions?.locationList || []).reduce((acc, l) => {
        acc[String(l.LocationID)] = l;
        return acc;
      }, {});
      const puLoc = locById[String(_data.pu)];
      const doLoc = locById[String(_data.do)];
      const puIsAirport = puLoc?.LocationTypeID === 2;
      const doIsAirport = doLoc?.LocationTypeID === 2;
      const derivedOption = puIsAirport && !doIsAirport ? "1" : "2";

      // Build the wall-clock datetime for one date by combining the calendar
      // pick with the typed time. Interpret it as America/New_York local time
      // via easternLocalToUtc — the whole operation runs on Eastern hours,
      // so whether the person doing Add Trip is in ET, Colombia, or anywhere
      // else, the same "5:00 PM" always stores as the same UTC value.
      const buildDatetime = (dateStr) => {
        const [y, mo, d] = dateStr.split("-").map((n) => parseInt(n, 10));
        const [hh, mi] = (time || "00:00").split(":").map((n) => parseInt(n, 10));
        return easternLocalToUtc(y, mo - 1, d, hh || 0, mi || 0).toISOString();
      };

      if (scheduleID) {
        // Edit mode — always a single date.
        const dateStr = dates[0];
        await updateTrip({
          ...rest,
          option: derivedOption,
          date: buildDatetime(dateStr),
          airlineCode: selectedAirlineCode,
          scheduleID,
        });
        return;
      }

      // Create mode — one row per selected date. Done sequentially so the
      // schedule/pickup/dropoff inserts inside createTrip don't race each
      // other and so a partial failure stops cleanly.
      let successCount = 0;
      const failures = [];
      const newlyCreatedIds = [];
      for (const dateStr of dates) {
        try {
          const result = await createTrip({
            ...rest,
            option: derivedOption,
            date: buildDatetime(dateStr),
            airlineCode: selectedAirlineCode,
          });
          if (result?.ScheduleID) newlyCreatedIds.push(result.ScheduleID);
          successCount++;
        } catch (err) {
          console.error(`createTrip failed for ${dateStr}`, err);
          failures.push(dateStr);
        }
      }

      // Stash the fresh IDs so SuperAdminContainer/CrewContainer can float
      // them to the top of the schedule list with a glow when the manager
      // returns to the previous page. Timestamp is used to expire the
      // highlight on next visit and stop it from lingering forever.
      if (newlyCreatedIds.length > 0) {
        try {
          const prev = JSON.parse(
            sessionStorage.getItem("recentlyCreatedTripIds") || "[]"
          );
          sessionStorage.setItem(
            "recentlyCreatedTripIds",
            JSON.stringify({
              ids: [...new Set([...(Array.isArray(prev) ? [] : prev.ids || []), ...newlyCreatedIds])],
              stampedAt: Date.now(),
            })
          );
        } catch (e) {
          console.warn("Could not stash recent trip ids:", e);
        }
      }

      if (successCount > 0) {
        toast.success(
          successCount === 1
            ? "Trip added successfully!"
            : `Added ${successCount} trips!`
        );
        queryClient.invalidateQueries({ queryKey: ["reportConditions"] });
        queryClient.invalidateQueries({ queryKey: ["searchConditions"] });

        // Post-save UX: reset the calendar so the next batch starts blank
        // (avoids accidentally re-submitting to the same dates twice) and
        // record a summary card at the top so the manager can see exactly
        // what was just saved.
        setValue("dates", []);
        const rawFlight = (_data.flight_no || "").trim();
        const flightForDisplay = rawFlight && !/-ADHOC$/i.test(rawFlight)
          ? `${rawFlight}-ADHOC`
          : rawFlight;
        const puLoc = locById[String(_data.pu)];
        const doLoc = locById[String(_data.do)];
        const airline = (conditions?.airlineList || []).find(
          (a) => String(a.AirlineID) === String(_data.airline)
        );
        setRecentAdds((prev) => [
          {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            addedAt: new Date(),
            dates: [...dates],
            time,
            flightNo: flightForDisplay,
            airlineCode: airline?.AirlineLetterCode || "",
            pickup: puLoc?.Code || _data.pu,
            dropoff: doLoc?.Code || _data.do,
            passengers: _data.passengers,
            direction: derivedOption === "1" ? "ARRIVE" : "DEPART",
          },
          ...prev,
        ]);
      }
      if (failures.length > 0) {
        toast.error(
          `Couldn't save ${failures.length} date${failures.length > 1 ? "s" : ""}: ${failures.join(", ")}`
        );
      }
    },
    [createTrip, updateTrip, scheduleID, selectedAirlineCode, conditions, setValue, queryClient]
  );

  const handleBack = useCallback(() => {
    sessionStorage.getItem("UserRole") === "1"
      ? navigate("/super-admin")
      : navigate("/manager");
  }, [navigate]);

  return isLoading ? null : (
    <div className="p-6 w-full">
      <HeaderContainer />
      {/* Top Section */}
      <div className="items-center mt-5 personal-info-content">
        <div
          onClick={handleBack}
          className="flex justify-between cursor-pointer items-center w-20 back-button"
        >
          <FaArrowLeft />
          <span>Back</span>
        </div>
        <p className="pt-4 font-semibold text-lg">
          Trip {!scheduleID ? "Add" : "Update"}
        </p>
        <span>{!scheduleID ? "Add" : "Update"} new trip here</span>

        {/* Session log of trips just added. Each Add-Trip click pushes a
            summary row here so the manager gets immediate confirmation
            without navigating back to the schedule list. Clears on page
            unload — only the current session shows. */}
        {recentAdds.length > 0 && !scheduleID && (
          <div
            style={{
              marginTop: 16,
              border: "1px solid #E5E1D8",
              borderRadius: 12,
              padding: 14,
              background: "linear-gradient(180deg,#FEF7E6 0%,#FDFCF9 100%)",
              maxHeight: 220,
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: "#92400E",
                  textTransform: "uppercase",
                }}
              >
                Just added ({recentAdds.reduce((n, a) => n + a.dates.length, 0)})
              </span>
              <button
                type="button"
                onClick={() => setRecentAdds([])}
                style={{
                  fontSize: 12,
                  color: "#4A5A73",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "2px 6px",
                }}
              >
                Clear list
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {recentAdds.map((r) => (
                <div
                  key={r.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    padding: "6px 10px",
                    background: "#FFFFFF",
                    border: "1px solid #F1EFEA",
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 4,
                        background: r.direction === "ARRIVE" ? "#DBEAFE" : "#FEE2E2",
                        color: r.direction === "ARRIVE" ? "#1D4ED8" : "#B91C1C",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                      }}
                    >
                      {r.direction}
                    </span>
                    <span style={{ fontFamily: "ui-monospace, monospace", fontWeight: 600 }}>
                      {r.airlineCode} {r.flightNo}
                    </span>
                    <span style={{ color: "#4A5A73" }}>
                      {r.pickup} → {r.dropoff}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, color: "#4A5A73", fontSize: 12 }}>
                    <span>
                      {r.dates.length === 1
                        ? r.dates[0]
                        : `${r.dates.length} dates`}
                    </span>
                    <span>{formatTimeForDisplay(r.time) || r.time}</span>
                    <span>{r.passengers} pax</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="w-100 mt-4 rounded-lg gap-1">
          <div className="flex justify-between mb-2">
            <div>
              <p>Company</p>
            </div>
            <Controller
              control={control}
              name="CompanyID"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between w-35">
                  <select
                    className="custom-input w-100 rounded-lg"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  >
                    {conditions.companyList.map((_company) => (
                      <option
                        value={_company.CompanyID}
                        key={`company-item-${_company.CompanyID}`}
                      >
                        {_company.Name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            />
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Airline</p>
            </div>
            <Controller
              control={control}
              name="airline"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between w-35">
                  <select
                    className="custom-input w-100 rounded-lg"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  >
                    {conditions.airlineList.map((_airline) => (
                      <option
                        value={_airline.AirlineID}
                        key={`company-item-${_airline.AirlineID}`}
                      >
                        {_airline.Description || _airline.Code}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            />
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>P/U</p>
            </div>
            <Controller
              control={control}
              name="pu"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between w-35">
                  <CustomSelect
                    id="pu-select"
                    size="wide"
                    options={locationOptions}
                    value={value}
                    onChange={onChange}
                  />
                </div>
              )}
            />
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>D/O</p>
            </div>
            <Controller
              control={control}
              name="do"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between w-35">
                  <CustomSelect
                    id="do-select"
                    size="wide"
                    options={locationOptions}
                    value={value}
                    onChange={onChange}
                  />
                </div>
              )}
            />
          </div>
          <div className="flex justify-between mb-2">
            <div><p>{dateLabel.replace("/Time", "")}</p></div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="dates"
                rules={{
                  validate: (v) => (v && v.length > 0) || "Pick at least one date",
                }}
                render={({ field: { value, onChange } }) => (
                  <CalendarPicker value={value || []} onChange={onChange} />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div><p>Time</p></div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="time"
                rules={{ required: "Time is required" }}
                render={({ field: { value, onChange } }) => (
                  <TimeTextInput
                    value={value}
                    onChange={onChange}
                    error={errors.time?.message || errors.dates?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Flight No</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="flight_no"
                rules={{
                  required: "Flight No is required",
                }}
                render={({ field: { value, onChange } }) => (
                  // Every Add-Trip row is billed as an ADHOC, so the history
                  // suggestions already carry the "-ADHOC" suffix — typing
                  // "1234" ghost-completes to "1234-ADHOC" and Tab accepts it.
                  // createTrip won't double-append if it's already there.
                  <FlightNoAutocomplete
                    value={value}
                    onChange={onChange}
                    options={flightNoOptions}
                    className="custom-input rounded-lg"
                    error={errors.flight_no?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Rate</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="rate"
                rules={{
                  required: "rate is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    type="number"
                    error={errors.rate?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Driver</p>
            </div>
            <Controller
              control={control}
              name="driver"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between w-35">
                  <select
                    className="custom-input w-100 rounded-lg"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  >
                    {conditions.driverList.map((_driver) => (
                      <option
                        value={_driver.DriverID}
                        key={`do-item-${_driver.DriverID}`}
                      >
                        {`${_driver.FirstName} ${_driver.LastName}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            />
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Vehicle</p>
            </div>
            <Controller
              control={control}
              name="vehicle"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between w-35">
                  <select
                    className="custom-input w-100 rounded-lg"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  >
                    {conditions.vehicleList.map((_vehicle) => (
                      <option
                        value={_vehicle.VehicleID}
                        key={`vehicle-item-${_vehicle.VehicleID}`}
                      >
                        {`${_vehicle.Code} - ${_vehicle.Model}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            />
          </div>

          {conditions.managerList && conditions.managerList.length > 0 && (
            <div className="flex justify-between mb-2">
              <div>
                <p>Manager</p>
              </div>
              <Controller
                control={control}
                name="managerId"
                render={({ field: { value, onChange } }) => (
                  <div className="flex justify-between w-35">
                    <select
                      className="custom-input w-100 rounded-lg"
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    >
                      <option value="">-- None --</option>
                      {conditions.managerList.map((_manager) => {
                        const name =
                          `${_manager.FirstName} ${_manager.LastName}`.trim() ||
                          _manager.Email;
                        const codes = (_manager.AirportCodes || []).join(", ");
                        return (
                          <option
                            value={_manager.UserID}
                            key={`manager-item-${_manager.UserID}`}
                          >
                            {codes ? `${name} — ${codes}` : name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
              />
            </div>
          )}

          <div className="flex justify-between mb-2">
            <div>
              <p>Passengers</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="passengers"
                rules={{
                  required: "passengers is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    type="number"
                    error={errors.passengers?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mt-4 mb-4 save-button">
            <button
              onClick={handleSubmit(handleSave)}
              disabled={isCreating | isUpdating}
              className="justify-center p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              {!scheduleID ? "Add" : "Update"}
            </button>
            <div>
              <div
                onClick={() => reset()}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Clear</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripAddContainer;
